import { Alert, AlertDescription, AlertTitle, Button, Card, CardContent, CardDescription, CardHeader, CardTitle, Input, Skeleton, useChat } from "randmarcomps";
import { AlertCircle, RefreshCcw, Search, ShieldCheck, Sparkles, Star, Plus } from "lucide-react";
import React from "react";
import { ManufacturerCategory, useGetV4ManufacturerByRouteManufacturerIdCategoriesQuery } from "randmar-api-client";
import CategoryMarginsGrid from "../components/categories/CategoryMarginsGrid";
import CreateCategory from "../components/categories/CreateCategory";
import { DataRefreshBar } from "../components/util/DataRefreshBar";
import InlineSpinner from "../components/util/InlineSpinner";
import { useManufacturer } from "../contexts/WithManufacturer";
import withRoot from "../withRoot";

type FilterOption = "all" | "starred" | "auto" | "requiresQualification";

function buildCategoriesAssistantPrompt(options: {
  applicationId: string;
  totalCategories: number;
  visibleCategories: number;
  searchTerm: string;
  statusFilter: FilterOption;
}) {
  const search = options.searchTerm.trim();
  const filter = options.statusFilter === "all" ? "All statuses" : options.statusFilter;

  return [
    "You are assisting with the Randmar Manufacturer Portal Categories page.",
    `Manufacturer applicationId: ${options.applicationId}`,
    `Total categories loaded: ${options.totalCategories}`,
    `Visible categories (after filtering): ${options.visibleCategories}`,
    `Current filters: status=${filter}${search ? `, search="${search}"` : ""}`,
    "",
    "Help me understand or improve what I'm seeing on this page.",
  ].join("\n");
}

const Categories: React.FC = () => {
  const { applicationId } = useManufacturer();
  const { sendMessageWithClear } = useChat();
  const [lastRefreshedAt, setLastRefreshedAt] = React.useState<Date | null>(null);

  const {
    data: categoriesData,
    isLoading,
    isFetching,
    isError,
    refetch,
  } = useGetV4ManufacturerByRouteManufacturerIdCategoriesQuery(
    { routeManufacturerId: applicationId },
    { skip: !applicationId }
  );
  const loading = isLoading;
  const categories: ManufacturerCategory[] = React.useMemo(
    () => (categoriesData as ManufacturerCategory[]) ?? [],
    [categoriesData]
  );
  const [searchTerm, setSearchTerm] = React.useState("");
  const [statusFilter, setStatusFilter] = React.useState<FilterOption>("all");
  const [showFilterSpinner, setShowFilterSpinner] = React.useState(false);

  React.useEffect(() => {
    if (!isFetching && categoriesData) {
      setLastRefreshedAt(new Date());
    }
  }, [categoriesData, isFetching]);

  React.useEffect(() => {
    const previousTitle = document.title;
    if (!previousTitle.toLowerCase().includes("categories")) {
      document.title = `Categories | ${previousTitle}`;
    }

    return () => {
      document.title = previousTitle;
    };
  }, []);

  React.useEffect(() => {
    if (loading) {
      setShowFilterSpinner(false);
      return;
    }
    if (searchTerm.trim() === "" && statusFilter === "all") {
      setShowFilterSpinner(false);
      return;
    }

    setShowFilterSpinner(true);
    const timer = setTimeout(() => setShowFilterSpinner(false), 400);
    return () => clearTimeout(timer);
  }, [searchTerm, statusFilter, loading]);

  const normalizedSearch = searchTerm.trim().toLowerCase();
  const filteredCategories = React.useMemo(() => {
    return categories.filter(category => {
      const matchesSearch =
        normalizedSearch.length === 0 ||
        [category.CategoryName, category.CategoryCode, category.Niche]
          .some(value => (value || "").toLowerCase().includes(normalizedSearch));

      const matchesStatus =
        statusFilter === "all" ||
        (statusFilter === "starred" && Boolean(category.Starred)) ||
        (statusFilter === "auto" && Boolean(category.AutoReorder)) ||
        (statusFilter === "requiresQualification" && Boolean(category.QualificationRequired));

      return matchesSearch && matchesStatus;
    });
  }, [categories, normalizedSearch, statusFilter]);

  const stats = React.useMemo(() => [
    { label: "Total categories", value: categories.length.toString() },
    { label: "Auto reorder", value: categories.filter(category => category.AutoReorder).length.toString() },
    { label: "Qualification required", value: categories.filter(category => category.QualificationRequired).length.toString() },
  ], [categories]);

  const filterOptions: { id: FilterOption; label: string; icon: React.ReactNode }[] = [
    { id: "all", label: "All statuses", icon: <Sparkles className="h-4 w-4" /> },
    { id: "starred", label: "Starred", icon: <Star className="h-4 w-4" /> },
    { id: "auto", label: "Auto reorder", icon: <RefreshCcw className="h-4 w-4" /> },
    { id: "requiresQualification", label: "Qualification", icon: <ShieldCheck className="h-4 w-4" /> },
  ];

  const assistantPrompt = React.useMemo(
    () =>
      buildCategoriesAssistantPrompt({
        applicationId,
        totalCategories: categories.length,
        visibleCategories: filteredCategories.length,
        searchTerm,
        statusFilter,
      }),
    [applicationId, categories.length, filteredCategories.length, searchTerm, statusFilter]
  );

  const pageContainerClassName = "mx-auto w-full max-w-[1440px] space-y-8 pb-10";
  const surfaceClassName = "rounded-2xl border border-gray-100 bg-white p-6 shadow-sm";

  return (
    <div className={pageContainerClassName} aria-busy={isFetching}>
      <header className={surfaceClassName} role="banner">
        <div className="flex flex-col gap-6 md:flex-row md:items-start md:justify-between">
          <div className="space-y-2">
            <div className="flex flex-wrap items-center gap-2">
              <span className="inline-flex items-center gap-2 rounded-full bg-[var(--ops-accent-muted)] px-3 py-1 text-xs font-semibold uppercase tracking-wide text-[var(--ops-accent)]">
                <Sparkles className="h-4 w-4" aria-hidden="true" />
                Manufacturer Portal
              </span>
              {isFetching && (
                <span className="inline-flex items-center gap-2 text-xs font-semibold uppercase tracking-wide text-slate-500">
                  <InlineSpinner size="sm" decorative />
                  Syncing
                </span>
              )}
            </div>
            <h1 className="text-[32px] font-semibold tracking-tight text-slate-900">Categories</h1>
            <p className="max-w-2xl text-[15px] text-slate-600">
              Organize assortments, monitor automation readiness, and keep partners aligned from a single workspace.
            </p>
          </div>

          <div className="flex flex-col gap-3 md:items-end">
            <DataRefreshBar
              align="right"
              lastRefreshedAt={lastRefreshedAt}
              isRefreshing={isFetching}
              onRefresh={() => refetch()}
              actions={(
                <Button
                  type="button"
                  size="icon"
                  variant="outline"
                  onClick={() => sendMessageWithClear(assistantPrompt)}
                  className="rounded-xl"
                  aria-label="Open assistant with this page context"
                >
                  <Sparkles className="h-4 w-4" aria-hidden="true" />
                </Button>
              )}
            />

            <CreateCategory
              categories={categories}
              renderTrigger={({ openDialog }) => (
                <Button onClick={openDialog} className="rounded-xl">
                  <Plus className="mr-2 h-4 w-4" aria-hidden="true" />
                  New category
                </Button>
              )}
            />
          </div>
        </div>

        <dl className="mt-6 grid gap-4 md:grid-cols-3">
          {stats.map(stat => (
            <div key={stat.label} className="rounded-2xl border border-gray-100 bg-white p-5 shadow-sm">
              <dt className="text-xs font-semibold uppercase tracking-wide text-slate-500">{stat.label}</dt>
              <dd className="mt-1 text-2xl font-semibold text-slate-900">{stat.value}</dd>
            </div>
          ))}
        </dl>

        {isError && (
          <Alert variant="destructive" className="mt-6 rounded-2xl border border-red-100 bg-white">
            <AlertTitle>Couldn’t load categories</AlertTitle>
            <AlertDescription>Try refreshing this page.</AlertDescription>
          </Alert>
        )}
      </header>

      <section className={[surfaceClassName, "space-y-4"].join(" ")} aria-label="Search and filters">
        <div className="flex flex-col gap-4 md:flex-row md:items-center md:justify-between">
          <div className="relative flex-1">
            <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" aria-hidden="true" />
            <Input
              value={searchTerm}
              onChange={(event) => setSearchTerm(event.target.value)}
              placeholder="Search by name, code, or niche"
              className="h-11 w-full rounded-lg border-slate-200 bg-white pl-9 text-[15px]"
              aria-label="Search categories"
            />
            {searchTerm && (
              <button
                type="button"
                onClick={() => setSearchTerm("")}
                className="absolute right-3 top-1/2 -translate-y-1/2 text-xs font-semibold uppercase tracking-wide text-slate-500 hover:text-[color:var(--ops-accent)]"
              >
                Clear
              </button>
            )}
          </div>

          <div className="flex flex-wrap gap-2">
            {filterOptions.map(option => {
              const isActive = option.id === statusFilter;
              const className = [
                "rounded-xl",
                isActive ? "border-transparent bg-[var(--ops-accent)] text-white hover:bg-[var(--ops-accent)]/90" : "",
              ]
                .filter(Boolean)
                .join(" ");

              return (
                <Button
                  type="button"
                  key={option.id}
                  aria-pressed={isActive}
                  onClick={() => setStatusFilter(option.id)}
                  variant="outline"
                  size="sm"
                  className={className}
                >
                  {option.icon}
                  {option.label}
                </Button>
              );
            })}
          </div>
        </div>

        {showFilterSpinner && (
          <div className="flex items-center gap-2 text-[13px] text-slate-500" aria-live="polite">
            <InlineSpinner size="sm" tone="accent" decorative />
            Updating results…
          </div>
        )}
      </section>

      {loading && categories.length === 0 ? (
        <CategoryGridSkeleton />
      ) : filteredCategories.length > 0 ? (
        <CategoryMarginsGrid applicationId={applicationId} categories={filteredCategories} />
      ) : categories.length === 0 ? (
        <CategoriesEmptyState categories={categories} />
      ) : (
        <NoResultsCard onReset={() => { setSearchTerm(""); setStatusFilter("all"); }} />
      )}
    </div>
  );
}

const CategoriesEmptyState: React.FC<{ categories: ManufacturerCategory[] }> = ({ categories }) => (
  <Card className="rounded-2xl border border-dashed border-gray-200 bg-white shadow-sm">
    <CardHeader className="items-center space-y-3 text-center">
      <div className="inline-flex items-center gap-2 rounded-full bg-[var(--ops-accent-muted)] px-3 py-1 text-xs font-semibold uppercase tracking-wide text-[var(--ops-accent)]">
        <Sparkles className="h-4 w-4" aria-hidden="true" /> No categories yet
      </div>
      <CardTitle className="text-2xl font-semibold text-slate-900">Design your first assortment</CardTitle>
      <CardDescription className="text-[15px] text-slate-600">
        Launch with confidence by adding a category. As soon as you publish, it becomes available to every reseller portal.
      </CardDescription>
    </CardHeader>
    <CardContent className="flex flex-col items-center gap-4 text-center">
      <CardDescription className="max-w-2xl text-[15px] text-slate-600">
        Stand up your assortment in minutes and keep buyers in sync across every portal.
      </CardDescription>
      <CreateCategory
        categories={categories}
        renderTrigger={({ openDialog }) => (
          <Button onClick={openDialog} size="lg" className="w-full max-w-xs">
            <Plus className="mr-2 h-4 w-4" /> Create a category
          </Button>
        )}
      />
      <p className="text-xs text-slate-500">You can fine-tune automation and qualification later.</p>
    </CardContent>
  </Card>
);

const NoResultsCard: React.FC<{ onReset: () => void }> = ({ onReset }) => (
  <Card className="rounded-2xl border border-dashed border-gray-200 bg-white shadow-sm">
    <CardHeader>
      <CardTitle className="text-xl font-medium text-slate-900">No categories match what you’re looking for</CardTitle>
      <CardDescription className="text-[15px] text-slate-600">
        Update your filters or clear search to see every category again.
      </CardDescription>
    </CardHeader>
    <CardContent className="flex flex-col gap-3 text-[15px] text-slate-600">
      <div className="inline-flex items-center gap-2 rounded-xl border border-rose-100 bg-rose-50 px-3 py-2">
        <AlertCircle className="h-4 w-4 text-rose-600" aria-hidden="true" />
        Filters can be combined. Make sure you’re not limiting results unintentionally.
      </div>
      <Button onClick={onReset} variant="outline" className="self-start rounded-xl">
        Reset filters
      </Button>
    </CardContent>
  </Card>
);

const CategoryGridSkeleton = () => (
  <div className="grid gap-5 sm:grid-cols-2 2xl:grid-cols-3">
    {Array.from({ length: 6 }).map((_, index) => (
      <Card key={`category-skeleton-${index}`} className="rounded-2xl border border-gray-100 bg-white shadow-sm">
        <CardContent className="space-y-5 p-6">
          <div className="flex items-center gap-4">
            <Skeleton className="h-16 w-16 rounded-xl" />
            <div className="flex-1 space-y-2">
              <Skeleton className="h-4 w-3/4" />
              <Skeleton className="h-3 w-1/2" />
            </div>
            <Skeleton className="h-8 w-8 rounded-full" />
          </div>
          <Skeleton className="h-3 w-full" />
          <Skeleton className="h-3 w-2/3" />
          <div className="grid gap-3 sm:grid-cols-3">
            <Skeleton className="h-12 rounded-xl" />
            <Skeleton className="h-12 rounded-xl" />
            <Skeleton className="h-12 rounded-xl" />
          </div>
          <Skeleton className="h-3 w-1/3" />
        </CardContent>
      </Card>
    ))}
  </div>
);

export default withRoot(Categories);
